import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { isResolved } from '../lib/resolution';
import { type IntakeItem, type ResolutionOption } from '../model/types';
import { UploadFileRow } from './UploadFileRow';
import { useTranslation } from 'react-i18next';

interface UploadConflictModalProps {
  open: boolean;
  items: IntakeItem[];
  canConfirm: boolean;
  onUpdateResolutionOption: (id: string, option: ResolutionOption) => void;
  onRename: (id: string, newName: string) => void;
  onRemove: (id: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function UploadConflictModal(props: UploadConflictModalProps) {
  const { t } = useTranslation('projects');

  const {
    open,
    items,
    canConfirm,
    onUpdateResolutionOption,
    onRename,
    onRemove,
    onConfirm,
    onCancel,
  } = props;

  const ordered = [
    ...items.filter((i) => i.existing != null),
    ...items.filter((i) => i.existing == null),
  ];

  const pending = items.filter((i) => !isResolved(i)).length;
  const summary =
    pending > 0
      ? t('projectFileUploadGuard.uploadConflictModal.filesNeedADecision', {
          pending,
          count: items.length,
        })
      : t('projectFileUploadGuard.uploadConflictModal.readyToUpload');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent scrollable size="lg" data-testid="upload-conflict-modal">
        <DialogHeader>
          <DialogTitle>{t('projectFileUploadGuard.uploadConflictModal.reviewUploads')}</DialogTitle>
          <DialogDescription>{summary}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-2">
          {/* `renderRow` was a thin pass-through to `UploadFileRow` — inlined
              directly at the map call site rather than kept as a separate closure. */}
          {ordered.map((item) => (
            <UploadFileRow
              key={item.id}
              item={item}
              onUpdateResolutionOption={onUpdateResolutionOption}
              onRename={onRename}
              onRemove={onRemove}
            />
          ))}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('projectFileUploadGuard.uploadConflictModal.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            data-testid="upload-conflict-confirm"
          >
            {t('projectFileUploadGuard.uploadConflictModal.uploadFiles', { count: items.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
